/**
 * The roster: people who have signed in, and people who have only been
 * assigned.
 *
 * A user document exists only once somebody has signed in with Google, so
 * before this the admin console could not see — let alone enrol — anyone who
 * had not opened the app yet. That is precisely backwards for onboarding: you
 * want the new Finance starter in the Finance department before their first
 * day, not after they think to go looking for the training.
 *
 * So enrolment is keyed by EMAIL, in its own `assignments` collection, and a
 * person's courses resolve from it whether or not an account exists. When they
 * do sign in, the two halves are matched on email and they are already
 * enrolled.
 *
 * This does not grant anybody access. Any @finder.com account can already sign
 * in — the app is open to the domain by design — so an assignment decides what
 * somebody is expected to do, never whether they may.
 */

const ALLOWED_DOMAINS = ['finder.com', 'findercrew.com']

/** Lower-cased and trimmed: the key an assignment is stored under. */
export function normaliseEmail(email) {
  return String(email || '').trim().toLowerCase()
}

export function isFinderEmail(email) {
  const at = normaliseEmail(email).split('@')
  return at.length === 2 && ALLOWED_DOMAINS.includes(at[1])
}

/**
 * Pull addresses out of whatever an admin pasted — a column from a
 * spreadsheet, a line from an email, a comma-separated list — and sort them
 * into what can be used and what cannot, rather than silently dropping half.
 */
export function parseEmailList(text) {
  const candidates = String(text || '')
    .split(/[\s,;]+/)
    .map((part) => normaliseEmail(part.replace(/^[<"']+|[>"',.]+$/g, '')))
    .filter(Boolean)

  const valid = []
  const invalid = []
  for (const candidate of candidates) {
    if (!isFinderEmail(candidate)) {
      if (!invalid.includes(candidate)) invalid.push(candidate)
    } else if (!valid.includes(candidate)) {
      valid.push(candidate)
    }
  }
  return { valid, invalid }
}

/**
 * One row per person, whether they have signed in, been assigned, or both.
 *
 * Matched on email because that is the only identifier the two halves share —
 * an assignment is made before a uid exists. Sorted by name where there is
 * one, so the people you can actually talk about come first.
 */
export function buildRoster(users = [], assignments = []) {
  const byEmail = new Map()

  for (const assignment of assignments) {
    const email = normaliseEmail(assignment.email)
    if (!email) continue
    byEmail.set(email, {
      email,
      uid: null,
      displayName: '',
      signedIn: false,
      assigned: true,
      department: assignment.department || null,
      degrees: assignment.degrees || [],
      managedBy: assignment.managedBy || null,
      // The two copies of the chain are kept SEPARATE all the way through.
      // They are allowed to disagree — that disagreement is the drift, and
      // merging it away is what made the first attempt's detector blind.
      assignedManagerChain: assignment.managerChain ?? null,
      accountManagerChain: null,
      lastLoginAt: null,
    })
  }

  for (const user of users) {
    const email = normaliseEmail(user.email)
    if (!email) continue
    const existing = byEmail.get(email)
    byEmail.set(email, {
      email,
      uid: user.uid,
      displayName: user.displayName || '',
      signedIn: true,
      assigned: !!existing?.assigned,
      // The assignment wins: it is what an admin edits, and a department left
      // on an old user document is a leftover from before this existed.
      department: existing?.department ?? user.department ?? null,
      degrees: existing?.assigned ? existing.degrees : (user.degrees || []),
      isAdmin: user.isAdmin === true,
      role: user.role || null,
      // Same rule as department: the assignment is what an admin edits, and
      // the user document is where it ends up once they have signed in.
      managedBy: existing?.assigned ? (existing.managedBy ?? user.managedBy ?? null) : (user.managedBy || null),
      // Same nullish test as managedBy above, deliberately. The first attempt
      // used a length test for the chain and `??` for the manager, so an
      // assignment holding a deliberately empty chain fell through to the user
      // document's stale one while managedBy did not — two coupled fields
      // disagreeing about who may see this person, silently and permissively.
      assignedManagerChain: existing?.assigned ? (existing.assignedManagerChain ?? null) : null,
      accountManagerChain: user.managerChain ?? null,
      lastLoginAt: user.lastLoginAt || null,
    })
  }

  return [...byEmail.values()].sort((a, b) => {
    if (a.signedIn !== b.signedIn) return a.signedIn ? -1 : 1
    return (a.displayName || a.email).localeCompare(b.displayName || b.email)
  })
}

/** Emails already on the roster, so adding them again is a no-op not a dupe. */
export function newEmailsOnly(emails = [], roster = []) {
  const known = new Set(roster.map((p) => normaliseEmail(p.email)))
  return emails.filter((e) => !known.has(normaliseEmail(e)))
}
